import { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import MetricRenderer from "../metricRenderer";
import LoaderThreeDots from "../loadingAnimation";
import { METRIC_KEY_DEFAULTS } from "../../utils/metricsRenderer";
import buscarMetricasDisponiveis from "../../utils/validarMetricas";

export default function GenericDashboard({
  filterData,
  api,
  dash,
  handleAplicarZoom,
  isZoomEnabled = true,
  setLoadingCronometro,
  setCounter,
  selectTime,
  optionsTime,
  startCronometro,
  controllerDashboard,
  setControllerDashboard,
  stopCronometro,
  customSlots = {}, // { [metricKey]: fn({ data, loading }) => ReactNode }
  children, // seções extras abaixo do grid (tabelas, etc)]
  schemaUiUrl = "/zabbix/dash/schemaUIConfig", // Cliente externo precisa sobrescrever para adicionar endpoint adequado
  serieTemporalUrl = "/zabbix/serieTemporal", // Cliente externo precisa sobrescrever para adicionar endpoint adequado
  fetchNullUiItems = false, // quando false, ignora keys com ui: null no fetch
  group = null, // quando definido, renderiza/busca apenas métricas com ui.group === group (usado p/ abas)
  itemsListUrl = "/zabbix/items/list", // Cliente externo precisa sobrescrever para adicionar endpoint adequado
  // Node renderizado quando o equipamento não tem nenhuma métrica deste dash/group
  // com item existente no Zabbix. Passar este slot é o que LIGA a validação —
  // sem ele o comportamento continua o de antes (grid vazio).
  semDadosSlot = null,
}) {
  const [uiSchema, setUiSchema] = useState({});
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [loadingSchema, setLoadingSchema] = useState(false);
  // Métricas com item no Zabbix; null = não validado ou validação indisponível
  const [metricasComItem, setMetricasComItem] = useState(null);
  // Evita exibir "sem dados" no primeiro paint, antes do schema chegar
  const [schemaCarregado, setSchemaCarregado] = useState(false);

  useEffect(() => {
    if (!filterData?.fabricante || !filterData?.funcao || !filterData?.modelo)
      return;
    let cancelled = false;
    setLoadingSchema(true);
    api
      ?.post(schemaUiUrl, {
        fabricante: filterData.fabricante,
        funcao: filterData.funcao,
        modelo: filterData.modelo,
        dash,
      })
      ?.then((r) => {
        if (!cancelled) setUiSchema(r?.data ?? {});
      })
      ?.catch(() => {
        if (!cancelled) setUiSchema({});
      })
      ?.finally(() => {
        if (!cancelled) {
          setLoadingSchema(false);
          setSchemaCarregado(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filterData?.fabricante, filterData?.funcao, filterData?.modelo, dash]);

  useEffect(() => {
    const keys = Object.keys(uiSchema);
    if (!keys.length || !filterData?.hostId || !filterData?.initialDate) return;

    let cancelled = false;
    setCounter?.(null);
    stopCronometro?.();
    setLoadingCronometro?.(true);

    const dateInitial =
      selectTime != null
        ? optionsTime?.[selectTime]?.value
        : filterData.initialDate;
    const finalDate =
      selectTime != null
        ? dayjs().set("second", 0).set("millisecond", 0).toDate()
        : filterData.finalDate;

    controllerDashboard?.abort();
    const controller = new AbortController();
    setControllerDashboard?.(controller);

    // ── Params inteligentes por device ──────────────────────────
    const isOnu = filterData?.device === "onu";
    const onuItem = isOnu ? (filterData?.interfaceId?.split(" - ") ?? []) : [];
    const commonParams = {
      hostids: filterData.hostId,
      device: filterData.device,
      fabricante: filterData.fabricante,
      funcao: filterData.funcao,
      modelo: filterData.modelo,
      dash,
      ...(isOnu && {
        serialOnu: onuItem?.[1]?.trim(),
        usernameCliente: onuItem?.[2]?.trim(),
        posicaoOnu: onuItem?.[0]?.trim(),
      }),
      ...(!isOnu &&
        filterData?.interfaceId && {
          interface: filterData.interfaceId,
        }),
    };

    const fetchKeys = (
      fetchNullUiItems
        ? keys
        : keys.filter((key) => uiSchema[key]?.ui !== null)
    ).filter((key) => {
      if (!group) return true;
      // group vem dos defaults por chave; mescla com o ui do schema p/ resolver
      const merged = { ...METRIC_KEY_DEFAULTS?.[key], ...uiSchema[key]?.ui };
      return merged?.group === group;
    });

    const encerrar = () => {
      if (!cancelled && !controller.signal.aborted) {
        setLoadingCronometro?.(false);
        startCronometro?.();
      }
    };

    (async () => {
      // Só valida quando o consumidor forneceu o slot de "sem dados"
      if (semDadosSlot) {
        const existentes = await buscarMetricasDisponiveis({
          api,
          filterData,
          dash,
          interface: commonParams?.interface,
          signal: controller.signal,
          itemsListUrl,
        });
        if (cancelled || controller.signal.aborted) return;

        setMetricasComItem(existentes);

        // Nenhuma métrica deste group existe no equipamento: nada a buscar
        if (
          Array.isArray(existentes) &&
          !fetchKeys.some((key) => existentes.includes(key))
        ) {
          encerrar();
          return;
        }
      }

      await Promise.all(
        fetchKeys.map((key) =>
          fetchMetric(
            key,
            uiSchema[key],
            commonParams,
            dateInitial,
            finalDate,
            controller.signal,
          ),
        ),
      );
      encerrar();
    })();

    return () => {
      cancelled = true;
    };
  }, [
    uiSchema,
    filterData?.hostId,
    filterData?.interfaceId,
    filterData?.initialDate,
    filterData?.finalDate,
    selectTime,
  ]);

  const fetchMetric = async (
    key,
    entry,
    commonParams,
    dateInitial,
    finalDate,
    signal,
  ) => {
    const config = entry?.ui ?? {};
    const extraParams = config?.fetchParams ?? {}; // ← params extras do schema

    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      if (config?.lastOnly) {
        const res = await api.post(
          serieTemporalUrl,
          {
            ...commonParams,
            ...extraParams,
            nameFilter: key,
            final_date: finalDate,
            lastItem: true,
          },
          { signal },
        );
        if (!signal.aborted)
          setData((p) => ({
            ...p,
            [key]: { currentValue: res?.data?.value ?? "N/A", data: [] },
          }));
      } else {
        const [history, last] = await Promise.all([
          api.post(
            serieTemporalUrl,
            {
              ...commonParams,
              ...extraParams,
              nameFilter: key,
              initial_date: dateInitial,
              final_date: finalDate,
            },
            { signal },
          ),
          api.post(
            serieTemporalUrl,
            {
              ...commonParams,
              ...extraParams,
              nameFilter: key,
              final_date: finalDate,
              lastItem: true,
            },
            { signal },
          ),
        ]);
        if (!signal.aborted) {
          const lastData = last?.data;
          const itemNames = {};
          if (lastData && typeof lastData === "object" && !Array.isArray(lastData)) {
            Object.entries(lastData).forEach(([k, entry]) => {
              if (entry?.name) itemNames[k] = entry.name;
            });
          }
          setData((p) => ({
            ...p,
            [key]: {
              currentValue: last?.data?.value ?? 0,
              data: history?.data ?? [],
              itemNames,
            },
          }));
        }
      }
    } catch {
      /* silêncio */
    } finally {
      if (!signal.aborted) setLoading((p) => ({ ...p, [key]: false }));
    }
  };

  // Mescla defaults por chave com o ui do schema (schema tem precedência)
  const resolveUi = (key, entry) => {
    if (entry?.ui == null) return null;
    return { ...METRIC_KEY_DEFAULTS?.[key], ...entry?.ui };
  };

  const entries = Object.entries(uiSchema);
  // Filtra apenas entradas com ui definido (ui: null = data-only, não renderiza)
  const visibleEntries = entries
    .map(([key, val]) => [key, { ...val, ui: resolveUi(key, val) }])
    .filter(([, v]) => v?.ui !== null)
    // Quando group está definido (abas), mostra só as métricas daquele grupo
    .filter(([, v]) => !group || v?.ui?.group === group)
    // Com validação ligada, esconde métricas sem item correspondente no Zabbix
    .filter(
      ([key]) =>
        !Array.isArray(metricasComItem) || metricasComItem.includes(key),
    );

  // Equipamento não tem esse dashboard: seção ausente no schema, ou nenhuma
  // métrica do group com item no Zabbix
  if (
    semDadosSlot &&
    schemaCarregado &&
    !loadingSchema &&
    filterData?.hostId &&
    visibleEntries.length === 0
  ) {
    return semDadosSlot;
  }
  // Cards (section card/both) respeitando a ordem das chaves do schema
  const cards = visibleEntries.filter(([, v]) =>
    ["card", "both"].includes(v?.ui?.section),
  );
  // Gráficos (section graph/both) respeitando a ordem das chaves do schema
  const graphs = visibleEntries.filter(([, v]) =>
    ["graph", "both"].includes(v?.ui?.section),
  );

  // Resolve o tamanho de grid do item — ui.colSpan sobrescreve o padrão
  const resolveGridCols = (ui, variant, totalCards) => {
    if (ui?.colSpan) return ui.colSpan;
    if (variant === "card") return { xs: 12, sm: 6, md: 12 / (totalCards || 1) };
    return { xs: 12, md: 6 };
  };

  const renderSlot = (key, val, variant) => {
    // Resolve o nome do customSlot indicado pelo schema de UI
    const schemaSlotName = val?.ui?.customSlot;
    // Payload completo passado ao customSlot para acesso a métricas irmãs
    const slotPayload = {
      data: data?.[key],
      loading: loading?.[key],
      config: val?.ui,
      allData: data,
      allLoading: loading,
    };
    // Se o customSlot do schema existir nas funções customizadas, executa-o
    if (schemaSlotName && customSlots?.[schemaSlotName]) {
      return customSlots[schemaSlotName](slotPayload);
    }
    // Se a própria chave da métrica existir nas funções customizadas, executa-a
    if (customSlots?.[key]) {
      return customSlots[key](slotPayload);
    }
    return (
      <MetricRenderer
        metricKey={key}
        config={val?.ui}
        data={data?.[key]}
        loading={loading?.[key]}
        variant={variant}
        handleAplicarZoom={handleAplicarZoom}
        isZoomEnabled={isZoomEnabled}
      />
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      <Grid container spacing={1}>
        {loadingSchema && (
          <Grid item xs={12}>
            <LoaderThreeDots />
          </Grid>
        )}

        {!loadingSchema && !!cards?.length && filterData?.hostId && (
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {cards?.map(([key, val]) => {
                const cols = resolveGridCols(val?.ui, "card", cards?.length);
                return (
                  <Grid item {...cols} key={key}>
                    {renderSlot(key, val, "card")}
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}

        {!loadingSchema &&
          filterData?.hostId &&
          graphs?.map(([key, val]) => {
            const cols = resolveGridCols(val?.ui, "graph", graphs?.length);
            return (
              <Grid item {...cols} key={key}>
                {renderSlot(key, val, "graph")}
              </Grid>
            );
          })}
      </Grid>

      {children}
    </Box>
  );
}
